import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./BoronRecordPage.css";

const STORAGE_KEY = "boron_records";
const API_URL = "https://bamboo.ngrok.dev/api/records";
const RECORDS_PER_PAGE = 20;
const REFRESH_INTERVAL_MS = 10000;
const EXPORT_COLUMN_ORDER = [
  "id",
  "pondNo",
  "date",
  "time",
  "borax",
  "deviceId",
  "boron",
];
const EXPORT_EXCLUDED_COLUMNS = ["createdAt", "updatedAt"];
const EXPORT_COLUMN_LABELS = {
  id: "ID",
  pondNo: "เลขบ่อ",
  date: "วันที่",
  time: "เวลา",
  borax: "ปริมาณสารบอแรกซ์ (%)",
  deviceId: "Device ID",
  boron: "ปริมาณสารบอแรกซ์ (boron)",
};

const POND_COLORS = [
  "#31b675", // Green
  "#ef5350", // Red
  "#2196f3", // Blue
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#00bcd4", // Cyan
  "#e91e63", // Pink
  "#795548", // Brown
  "#607d8b", // Blue Grey
  "#ffc107", // Amber
];

const mockData = [
  { id: 1, pondNo: "1", date: "2026-04-20", boron: 20 },
  { id: 2, pondNo: "2", date: "2026-04-20", boron: 10 },
  { id: 3, pondNo: "1", date: "2026-04-21", boron: 5 },
  { id: 4, pondNo: "2", date: "2026-04-21", boron: 10 },
  { id: 5, pondNo: "1", date: "2026-04-22", boron: 5 },
  { id: 6, pondNo: "2", date: "2026-04-22", boron: 3 },
  { id: 7, pondNo: "1", date: "2026-04-26", boron: 5 },
  { id: 8, pondNo: "2", date: "2026-04-26", boron: 5 },
];

export default function BoronRecordPage({ onLogout, currentUser = "admin" }) {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pondNo, setPondNo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState(new Date().toTimeString().split(" ")[0].slice(0, 5));
  const [boron, setBoron] = useState("");

  const [filterEnd, setFilterEnd] = useState(new Date().toISOString().split("T")[0]);
  const [filterStart, setFilterStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [selectedPonds, setSelectedPonds] = useState([]);

  const [waterVolume, setWaterVolume] = useState("");
  const [targetPercent, setTargetPercent] = useState("");
  const [calcResult, setCalcResult] = useState(null);

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");

  const [isPondDropdownOpen, setIsPondDropdownOpen] = useState(false);
  const [timeRange, setTimeRange] = useState("all");

  const mapRecords = useCallback((data) => {
    return data.map((item) => ({
      ...item,
      boron: item.borax !== undefined ? item.borax : item.boron,
    }));
  }, []);

  const fetchRecords = useCallback(async ({ fallbackToLocal = false } = {}) => {
    try {
      const response = await fetch(API_URL);
      if (!response.ok) {
        throw new Error("Failed to load records");
      }

      const data = await response.json();
      setRecords(mapRecords(data));
    } catch (error) {
      console.warn("API load failed:", error);

      if (fallbackToLocal) {
        const saved = localStorage.getItem(STORAGE_KEY);
        setRecords(saved ? JSON.parse(saved) : mockData);
      }
    }
  }, [mapRecords]);

  useEffect(() => {
    fetchRecords({ fallbackToLocal: true });

    const intervalId = window.setInterval(() => {
      fetchRecords();
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [fetchRecords]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStart, filterEnd, selectedPonds, timeRange]);

  useEffect(() => {
    if (records.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    }
  }, [records]);

  const filteredRecords = useMemo(() => {
    return records
      .filter((item) => {
        if (timeRange !== "all") {
          const now = new Date();
          const timePart = item.time || "00:00";
          const itemTime = new Date(`${item.date}T${timePart}`);

          if (isNaN(itemTime.getTime())) return false;

          const diffMs = now - itemTime;
          const diffMin = diffMs / (1000 * 60);

          if (timeRange === "12h" && diffMin > 12 * 60) return false;
          if (timeRange === "6h" && diffMin > 6 * 60) return false;
          if (timeRange === "1h" && diffMin > 60) return false;
          if (timeRange === "30m" && diffMin > 30) return false;

          if (diffMs < -60000) return false;
        } else {
          if (filterStart && item.date < filterStart) return false;
          if (filterEnd && item.date > filterEnd) return false;
        }

        if (selectedPonds.length > 0 && !selectedPonds.includes(item.pondNo))
          return false;
        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time || "00:00"}`);
        const dateB = new Date(`${b.date}T${b.time || "00:00"}`);
        return dateB - dateA;
      });
  }, [records, filterStart, filterEnd, selectedPonds, timeRange]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / RECORDS_PER_PAGE));
  const pageStart = (currentPage - 1) * RECORDS_PER_PAGE;
  const paginatedRecords = filteredRecords.slice(pageStart, pageStart + RECORDS_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleFilter = () => { };

  const chartData = useMemo(() => {
    const grouped = {};

    filteredRecords.forEach((item) => {
      const label = item.time ? `${item.date} ${item.time}` : item.date;
      if (!grouped[label]) {
        grouped[label] = {
          label,
          date: item.date,
          time: item.time || "00:00",
        };
      }

      grouped[label][`บ่อ ${item.pondNo}`] = Number(item.boron);
    });

    return Object.values(grouped).sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time}`);
      const dateB = new Date(`${b.date} ${b.time}`);
      return dateA - dateB;
    });
  }, [filteredRecords]);

  const pondList = useMemo(() => {
    return [...new Set(records.map((item) => item.pondNo))].sort();
  }, [records]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pondNo || !date || !time || !boron) {
      alert("กรุณากรอกข้อมูลให้ครบ");
      return;
    }

    const payload = {
      pondNo,
      date,
      time,
      borax: Number(boron),
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to save record");
      }

      const savedData = await response.json();

      setRecords((prev) => [
        {
          ...payload,
          id: savedData.id || Date.now(),
          boron: Number(boron),
        },
        ...prev,
      ]);
      fetchRecords();

      setPondNo("");
      setBoron("");
      setTime(new Date().toTimeString().split(" ")[0].slice(0, 5));
    } catch (error) {
      console.error("API Error:", error);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูลไปยังเซิร์ฟเวอร์");
    }
  };



  const handleDelete = async (id) => {
    if (window.confirm("ต้องการลบข้อมูลนี้ใช่ไหม?")) {
      try {
        const response = await fetch(`${API_URL}/${id}`, {
          method: "DELETE",
        });

        if (response.ok) {
          setRecords((prev) => prev.filter((item) => item.id !== id));
          fetchRecords();
        } else {
          throw new Error("Failed to delete record");
        }
      } catch (error) {
        console.error("Delete API Error:", error);
        alert("ไม่สามารถลบข้อมูลจากเซิร์ฟเวอร์ได้");
      }
    }
  };

  const togglePond = (pond) => {
    setSelectedPonds((prev) =>
      prev.includes(pond) ? prev.filter((p) => p !== pond) : [...prev, pond]
    );
  };

  const handleCalculate = () => {
    const volume = Number(waterVolume);
    const target = Number(targetPercent);

    if (!volume || !target) {
      alert("กรุณากรอกปริมาตรน้ำและค่า % ที่ต้องการ");
      return;
    }

    // สมมติคำนวณแบบง่าย: ปริมาณสาร = ปริมาตรน้ำ x target% / 100
    const amount = (volume * target) / 100;
    setCalcResult(amount);
  };

  const handleExportExcel = () => {
    setExportStart(filterStart);
    setExportEnd(filterEnd);
    setIsExportModalOpen(true);
  };

  const getExportColumns = (data) => {
    const keys = new Set();
    data.forEach((item) => {
      Object.keys(item)
        .filter((key) => !EXPORT_EXCLUDED_COLUMNS.includes(key))
        .forEach((key) => keys.add(key));
    });

    return [
      ...EXPORT_COLUMN_ORDER.filter((key) => keys.has(key)),
      ...[...keys]
        .filter((key) => !EXPORT_COLUMN_ORDER.includes(key))
        .sort(),
    ];
  };

  const formatCsvValue = (value) => {
    if (value === null || value === undefined) return "";

    const text =
      typeof value === "object" ? JSON.stringify(value) : String(value);

    return `"${text.replace(/"/g, '""')}"`;
  };

  const downloadCsv = (data, filename) => {
    const columns = getExportColumns(data);
    const header = columns.map((key) => EXPORT_COLUMN_LABELS[key] || key);
    const rows = data.map((item) =>
      columns.map((key) => formatCsvValue(item[key])).join(",")
    );
    const csv = [header.map(formatCsvValue).join(","), ...rows].join("\n");
    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const confirmExport = async () => {
    try {
      const params = new URLSearchParams();
      if (exportStart) params.append("start", exportStart);
      if (exportEnd) params.append("end", exportEnd);

      const response = await fetch(
        `${API_URL}?${params.toString()}`
      );

      if (!response.ok) throw new Error("Failed to fetch data for export");

      const data = await response.json();

      if (data.length === 0) {
        alert("ไม่พบข้อมูลในช่วงวันที่เลือก");
        return;
      }

      downloadCsv(
        data,
        `boron-records-${exportStart || "all"}-to-${exportEnd || "all"}.csv`
      );

      setIsExportModalOpen(false);
    } catch (error) {
      console.error("Export Error:", error);
      alert("เกิดข้อผิดพลาดในการส่งออกข้อมูล");
    }
  };

  return (
    <div className="boron-page">
      <div className="boron-card">
        <section className="section">
          <div className="top-header-bar">
            <h1>
              <span></span> 🪵 ระบบบันทึกปริมาณสารบอแรกซ์ในน้ำแช่ไม้ไผ่
            </h1>
            {onLogout && (
              <div className="user-nav-badge">
                <span className="user-nav-info">
                  <span className="user-status-dot"></span>
                  ผู้ดูแลระบบ: <strong>{currentUser}</strong>
                </span>
                <button
                  type="button"
                  onClick={onLogout}
                  className="logout-nav-btn"
                  title="ออกจากระบบ"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: 5 }}
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  ออกจากระบบ
                </button>
              </div>
            )}
          </div>

          <form className="form-grid" onSubmit={handleSubmit}>
            <div>
              <label>เลขที่บ่อแช่:</label>
              <input
                value={pondNo}
                onChange={(e) => setPondNo(e.target.value)}
                placeholder="เช่น 1"
              />
            </div>

            <div>
              <label>วันที่วัด:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div>
              <label>เวลาที่วัด:</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>

            <div>
              <label>ปริมาณสารบอแรกซ์ (%):</label>
              <input
                type="number"
                step="0.01"
                value={boron}
                onChange={(e) => setBoron(e.target.value)}
                placeholder="เช่น 5.0"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                บันทึกข้อมูล
              </button>
            </div>
          </form>
        </section>

        <section className="section">
          <h2>📈 กราฟแสดงแนวโน้มปริมาณสารบอแรกซ์</h2>

          <div className="filter-row">
            <label>เลขบ่อ:</label>
            <div className="custom-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setIsPondDropdownOpen(!isPondDropdownOpen)}
              >
                {selectedPonds.length === 0
                  ? "ทั้งหมด"
                  : `เลือกแล้ว (${selectedPonds.length})`}
                <span className={`arrow ${isPondDropdownOpen ? "up" : ""}`}>
                  ▼
                </span>
              </button>

              {isPondDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedPonds.length === 0}
                        onChange={() => setSelectedPonds([])}
                      />
                      แสดงทั้งหมด
                    </label>
                  </div>
                  <hr />
                  {pondList.map((pond) => (
                    <div key={pond} className="dropdown-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedPonds.includes(pond)}
                          onChange={() => togglePond(pond)}
                        />
                        บ่อ {pond}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="time-filter">
              <label>ช่วงเวลา:</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option value="all">กำหนดเอง (ระบุวันที่)</option>
                <option value="12h">12 ชั่วโมงล่าสุด</option>
                <option value="6h">6 ชั่วโมงล่าสุด</option>
                <option value="1h">1 ชั่วโมงล่าสุด</option>
                <option value="30m">30 นาทีล่าสุด</option>
              </select>
            </div>

            {timeRange === "all" && (
              <>
                <label>ช่วงวันที่:</label>
                <input
                  type="date"
                  value={filterStart}
                  onChange={(e) => setFilterStart(e.target.value)}
                />
                <span>ถึง</span>
                <input
                  type="date"
                  value={filterEnd}
                  onChange={(e) => setFilterEnd(e.target.value)}
                />
              </>
            )}
            <button
              type="button"
              className="filter-btn"
              onClick={handleFilter}
            >
              กรองข้อมูล
            </button>
          </div>

          <div className="chart-box">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Legend />
                {pondList
                  .filter((pond) => selectedPonds.length === 0 || selectedPonds.includes(pond))
                  .map((pond, index) => (
                    <Line
                      key={pond}
                      type="monotone"
                      dataKey={`บ่อ ${pond}`}
                      stroke={POND_COLORS[index % POND_COLORS.length]}
                      strokeWidth={2}
                      dot
                      connectNulls
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="section calculator">
          <h2>🧮 เครื่องคำนวณการเติมสารบอแรกซ์</h2>

          <div className="form-grid two">
            <div>
              <label>ปริมาตรน้ำในบ่อ (ลิตร):</label>
              <input
                type="number"
                value={waterVolume}
                onChange={(e) => setWaterVolume(e.target.value)}
                placeholder="เช่น 5000"
              />
            </div>

            <div>
              <label>ค่า % ที่ต้องการ (Target %):</label>
              <input
                type="number"
                step="0.01"
                value={targetPercent}
                onChange={(e) => setTargetPercent(e.target.value)}
                placeholder="เช่น 7.0"
              />
            </div>

            <button type="button" onClick={handleCalculate}>
              คำนวณปริมาณที่ต้องเติม
            </button>
          </div>

          {calcResult !== null && (
            <p className="result">
              ต้องเติมสารบอริกประมาณ <b>{calcResult.toFixed(2)}</b> หน่วย
            </p>
          )}
        </section>

        <section className="section">
          <div className="section-header">
            <h2>📄 ประวัติการบันทึกข้อมูล</h2>
            <button className="export-btn" onClick={handleExportExcel}>
              📥 Export เป็น CSV
            </button>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>เลขบ่อ</th>
                  <th>วันที่</th>
                  <th>เวลา</th>
                  <th>ปริมาณ (%)</th>
                  <th>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.map((item) => (
                  <tr key={item.id}>
                    <td>{item.pondNo}</td>
                    <td>{item.date}</td>
                    <td>{item.time || "-"}</td>
                    <td>{Number(item.boron).toFixed(1)} %</td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(item.id)}
                        title="ลบข้อมูล"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}

                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="5">ไม่พบข้อมูล</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredRecords.length > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                แสดง {pageStart + 1}-{Math.min(pageStart + RECORDS_PER_PAGE, filteredRecords.length)} จาก {filteredRecords.length} รายการ
              </div>
              <div className="pagination-actions">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                >
                  ก่อนหน้า
                </button>
                <span>
                  หน้า {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                >
                  ถัดไป
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {isExportModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>📥 เลือกช่วงวันที่ต้องการส่งออก CSV</h3>
            <div className="form-grid">
              <div>
                <label>วันที่เริ่มต้น:</label>
                <input
                  type="date"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                />
              </div>
              <div>
                <label>วันที่สิ้นสุด:</label>
                <input
                  type="date"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setIsExportModalOpen(false)}
              >
                ยกเลิก
              </button>
              <button className="confirm-btn" onClick={confirmExport}>
                ตกลง (Export)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
